import { useQuery } from '@apollo/client';
import { useContext, type ReactNode } from 'react';
import { upperFirst } from 'lodash-es';
import {
    CustomReportTemplatesDocument,
    FileType,
    ReportTemplatesLegacyDocument,
    ReportTemplateEntityType,
    type CustomReportTemplatesQuery,
    type CustomReportTemplatesQueryVariables,
    type ReportTemplatesLegacyQuery,
    type ReportTemplatesLegacyQueryVariables,
} from '@/graphql/types';
import { useObservableState } from 'observable-hooks';
import { map } from 'rxjs';
import { useProfile } from '../../../profile';
import classNames from '../../../utils/classNames';
import { ObjectType } from '../../../utils/objectType';
import { extractNodes } from '../../../utils/relay';
import { FILE_TYPES_DISPLAY_TEXT } from './utils';
import BetaBadge from '../../betaBadge/BetaBadge';
import ExportContext, { type ExportDataSet } from './ExportContext';

type LegacyTemplate = {
    id: number;
    name: string;
};

interface ListItemProps {
    onClick?: () => void;
    text: string;
    beta?: boolean;
}

interface ButtonOrDivProps {
    children: ReactNode;
    className: string;
    onClick?: () => void;
}

function ButtonOrDiv({ children, className, onClick }: ButtonOrDivProps) {
    if (onClick) {
        return (
            <button type="button" onClick={onClick} className={className}>
                {children}
            </button>
        );
    }

    return <div className={className}>{children}</div>;
}

function ListItem({ onClick, text, beta }: ListItemProps) {
    return (
        <li
            className={classNames(
                'bg-menu-background relative mb-2 box-border cursor-default whitespace-nowrap',
                onClick && 'hover:bg-menu-hover'
            )}
        >
            <ButtonOrDiv
                className="text-menu-color w-full py-2 pl-2 text-left font-bold"
                onClick={onClick}
            >
                {beta && <BetaBadge className="mr-0.5" />} {text}
            </ButtonOrDiv>
        </li>
    );
}

interface MessageProps {
    message?: string;
}

function Message({ message }: MessageProps) {
    return <span className="text-menu-color pl-2 no-underline">{message}</span>;
}

function ExportTypes() {
    const context = useContext(ExportContext);
    const exportTypes = Object.values(FileType);

    if (!context) {
        return null;
    }

    const { createGraphReport, openDatePickerForExportType } = context;

    return (
        <>
            {exportTypes.map((exportType) => (
                <ListItem
                    onClick={
                        exportType === FileType.Pdf
                            ? () => createGraphReport(FileType.Pdf)
                            : () => openDatePickerForExportType(exportType)
                    }
                    text={FILE_TYPES_DISPLAY_TEXT[exportType]}
                    key={exportType}
                />
            ))}
        </>
    );
}

function LegacyReportTemplates() {
    const { canAccessLegacyPdfTemplates } = useProfile();
    const { loading, error, data } = useQuery<
        ReportTemplatesLegacyQuery,
        ReportTemplatesLegacyQueryVariables
    >(ReportTemplatesLegacyDocument, { skip: !canAccessLegacyPdfTemplates });
    const context = useContext(ExportContext);

    if (!context) {
        return null;
    }

    const { createGraphReport } = context;

    if (!canAccessLegacyPdfTemplates) {
        return null;
    }

    if (loading) return <Message message={gettext('REPORT_TEMPLATES_LOADING')} />;
    if (error) return <Message message={gettext('REPORT_TEMPLATES_LOADING_ERROR')} />;

    const templates: LegacyTemplate[] = (data?.reportTemplatesLegacy ?? []).filter(
        (template): template is LegacyTemplate =>
            template != null && typeof template.id === 'number' && typeof template.name === 'string'
    );

    return (
        <>
            {templates.map((template) => (
                <ListItem
                    key={template.id}
                    text={template.name}
                    onClick={() => createGraphReport(FileType.Pdf, { templateId: template.id })}
                />
            ))}
        </>
    );
}

function getProjectId(measuringPointId: string | null) {
    if (!measuringPointId) {
        return null;
    }

    // This selector reads metadata from the legacy jQuery measuring-point
    // selector until that widget is replaced (tracked in #7770).
    const option = document.querySelector<HTMLOptionElement>(
        `#measuring-point-selector option[value="${measuringPointId}"]`
    );

    return option?.getAttribute('data-project-id') || null;
}

function getProjectId$(dataSet: ExportDataSet) {
    return dataSet.currentSensor$.pipe(map(getProjectId));
}

function getEntityTypeTranslation(entityType: ReportTemplateEntityType): string {
    switch (entityType) {
        case ReportTemplateEntityType.Project:
            return ObjectType.PROJECT.translation;
        case ReportTemplateEntityType.MeasuringPoint:
            return ObjectType.MEASURING_POINT.translation;
        default:
            throw new Error(`Unsupported report template entity type: ${String(entityType)}`);
    }
}

function CustomReportTemplates() {
    const { loading, error, data } = useQuery<
        CustomReportTemplatesQuery,
        CustomReportTemplatesQueryVariables
    >(CustomReportTemplatesDocument);
    const context = useContext(ExportContext);

    if (!context) {
        throw new Error('CustomReportTemplates must be used within ExportContext.Provider');
    }

    const { createGraphReport, dataSet } = context;
    const projectId = useObservableState(getProjectId$(dataSet), null);

    if (loading) return <Message message={gettext('REPORT_TEMPLATES_LOADING')} />;
    if (error) return <Message message={gettext('REPORT_TEMPLATES_LOADING_ERROR')} />;

    type TemplateNode = NonNullable<
        NonNullable<
            NonNullable<CustomReportTemplatesQuery['reportTemplates']>['edges'][number]
        >['node']
    >;
    type TemplateGroup = {
        entityType: ReportTemplateEntityType;
        items: TemplateNode[];
        isProjectTemplates: boolean;
        entityTypeLabel: string;
    };

    const templates: TemplateNode[] = extractNodes(data?.reportTemplates?.edges);

    const orderedEntityTypes = [
        ReportTemplateEntityType.Project,
        ReportTemplateEntityType.MeasuringPoint,
    ];
    const visibleTemplateGroups = orderedEntityTypes
        .map((entityType) => {
            const items = templates.filter((template) => template.entityType === entityType);
            const isProjectTemplates = entityType === ReportTemplateEntityType.Project;

            // If this measuring point has no project configured, we don't show the project
            // template options.
            if (!items.length || (isProjectTemplates && !projectId)) {
                return null;
            }

            return {
                entityType,
                items,
                isProjectTemplates,
                entityTypeLabel: getEntityTypeTranslation(entityType),
            };
        })
        .filter((group): group is TemplateGroup => group !== null);
    const shouldShowEntityTypeHeadings = visibleTemplateGroups.length > 1;

    return (
        <>
            {visibleTemplateGroups.flatMap(
                ({ entityType, items, isProjectTemplates, entityTypeLabel }) => {
                    const heading = shouldShowEntityTypeHeadings
                        ? [<ListItem key={entityType} text={`${upperFirst(entityTypeLabel)}:`} />]
                        : [];

                    return [
                        <hr key={`${entityType}-divider`} />,
                        ...heading,
                        ...items.map((template) => (
                            <ListItem
                                key={`${entityType}-${template.id}`}
                                text={template.name}
                                onClick={() =>
                                    createGraphReport(FileType.Pdf, {
                                        omnidotsTemplateId: template.id,
                                        ...(isProjectTemplates ? { projectId } : {}),
                                    })
                                }
                            />
                        )),
                    ];
                }
            )}
        </>
    );
}

function ExportChoices() {
    return (
        <ul>
            <ExportTypes />
            <LegacyReportTemplates />
            <CustomReportTemplates />
        </ul>
    );
}

export default ExportChoices;
